import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import type { ApiResponse, ResearchFocusArea } from '@/types';
import { toast } from 'sonner';
import { ChevronDown, Loader2, MapPin, Sparkles } from 'lucide-react';

type Props = {
  scope: 'strategy' | 'standalone';
  strategyId?: string;
  strategyTitle?: string;
  strategyYear?: number;
  focusArea: ResearchFocusArea;
  draftQuery: string;
  onAppendToQuery: (text: string) => void;
  disabled?: boolean;
};

/**
 * Zusätzliche KI-Aktionen für die Research-Frage (Companion-ähnlich: Dropdown statt nur ein Button).
 */
export function ResearchQueryHelper({
  scope,
  strategyId,
  strategyTitle,
  strategyYear,
  focusArea,
  draftQuery,
  onAppendToQuery,
  disabled,
}: Props) {
  const [loading, setLoading] = useState(false);

  const suggestLocations = async () => {
    setLoading(true);
    try {
      let res: ApiResponse<{ markdown: string }>;
      if (scope === 'strategy' && strategyId) {
        res = await api.post<ApiResponse<{ markdown: string }>>(
          `/marketing-strategies/${strategyId}/research/suggest-locations`,
          { focus: focusArea, draft_query: draftQuery.trim() || undefined }
        );
      } else {
        res = await api.post<ApiResponse<{ markdown: string }>>('/ai/research/suggest-locations', {
          year: strategyYear ?? new Date().getFullYear(),
          title: strategyTitle ?? 'Research',
          focus: focusArea,
          draft_query: draftQuery.trim() || undefined,
        });
      }
      if (!res.success || !res.data?.markdown?.trim()) {
        toast.error(res.error ?? 'Keine Orte erhalten.');
        return;
      }
      const block = `---\n**Orte & Regionen (KI-Vorschlag)**\n\n${res.data.markdown.trim()}`;
      onAppendToQuery(block);
      toast.success('Orte wurden angefügt — Text bei Bedarf kürzen.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Orte konnten nicht erzeugt werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-1 text-xs"
          disabled={disabled || loading}
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          Mehr
          <ChevronDown size={12} className="opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(100vw-2rem,280px)]">
        <DropdownMenuItem
          className="gap-2 text-xs"
          onClick={() => void suggestLocations()}
          disabled={loading}
        >
          <MapPin size={14} className="shrink-0 text-muted" />
          <span className="flex flex-col gap-0.5">
            <span className="font-medium">Orte generieren</span>
            <span className="text-[10px] font-normal text-muted">
              Regionen & Mikromärkte (DACH) als Markdown anhängen
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
